import { Box, IconButton } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePaymentMethods } from 'src/features/company/companySlice';
import { deletePaymentMethod, editPaymentMethod, getPaymentMethods } from 'src/features/company/utils';
import { AppDispatch, RootState } from 'src/store/store';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import StarIcon from '@mui/icons-material/Star';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { Column } from 'src/utils/constants/table-constants';
import { Elements } from '@stripe/react-stripe-js';
import BkTable from 'src/components/table/BkTable';
import { Button } from 'src/components/new/Button';
import { AddPaymentMethod } from './modals/AddPaymentMethod';
import { Spacer } from 'src/components/new/Spacer';
import { Typography } from 'src/components/new/Typography';

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!stripeKey) {
  throw new Error('Missing Stripe publishable key in environment variables');
}

const stripePromise = loadStripe(stripeKey);

const columns = [
  {
    headerName: 'Nickname',
    field: 'nickname',
    headerCustomStyles: {
      width: '15rem',
    },
  },
  {
    headerName: 'Payment Type',
    field: 'type',
    headerCustomStyles: {
      width: '10rem',
    },
  },
  {
    headerName: 'Acct. Ending',
    field: 'acctEnding',
    headerCustomStyles: {
      width: '7.8125rem',
    },
  },
  {
    headerName: 'Card Expiration',
    field: 'expiration',
    headerCustomStyles: {
      width: '8.75rem',
    },
  },
  {
    headerName: 'Billing Address',
    field: 'billingAddress',
    headerCustomStyles: {
      width: '28.75rem',
    },
  },
  {
    headerName: 'Action',
    field: 'action',
    headerCustomStyles: {
      width: '8.125rem',
    },
  },
];

const preparePaymentMethodData = (paymentMethods: any[]) => {
  return paymentMethods.map((method) => ({
    id: method['id'],
    nickname: method['billing_details']['name'],
    type: method?.card?.display_brand ? 'Credit Card' : 'ACH',
    acctEnding: method['card'] ? method['card']['last4'] : method['us_bank_account']['last4'],
    expiration: method['card'] ? `${method['card']['exp_month']}/${method['card']['exp_year']}` : '',
    billingAddress: method['billing_details']['address']['line1'],
    isDefault: method['metadata']['is_default'] === 'true' ? true : false,
  }));
};

interface PaymentMethodsSectionProps {
  setShowSnackbarMsg: Dispatch<SetStateAction<{ type: 'success' | 'error'; msg: string }>>;
}

export const PaymentMethodsSection = ({ setShowSnackbarMsg }: PaymentMethodsSectionProps) => {
  const [isAddMethodModalOpen, setIsAddMethodModalOpen] = useState(false);
  const dispatch: AppDispatch = useDispatch();

  const [paymentMethodToEdit, setPaymentMethodToEdit] = useState<any>(null);

  const paymentMethods = useSelector((state: RootState) => state.company.paymentMethods);
  const editingPaymentMethod = useSelector((state: RootState) => state.company.editingPaymentMethod);

  const handleAddPaymentMethod = () => {
    setPaymentMethodToEdit(null);
    setIsAddMethodModalOpen(false);
  };

  const makePaymentMethodDefault = (paymentMethodId: string) => {
    const currentMethods = paymentMethods;

    const updatedMethods = paymentMethods.map((method) =>
      method.id === paymentMethodId
        ? { ...method, metadata: { is_default: 'true' } }
        : { ...method, metadata: { is_default: 'false' } },
    );
    dispatch(updatePaymentMethods(updatedMethods));
    preparePaymentMethodData(updatedMethods);
    dispatch(editPaymentMethod({ isDefault: true, paymentMethodId })).then((result) => {
      if (editPaymentMethod.fulfilled.match(result)) {
        setShowSnackbarMsg({ type: 'success', msg: 'Successfully changed default method' });
      }
      if (editPaymentMethod.rejected.match(result)) {
        dispatch(updatePaymentMethods(currentMethods));
        preparePaymentMethodData(currentMethods);
        setShowSnackbarMsg({ type: 'error', msg: 'Something went wrong' });
      }
    });
  };

  const onEditPaymentClick = (paymentMethodId: string) => {
    const paymentMethod = paymentMethods.find((method) => method.id === paymentMethodId);
    setPaymentMethodToEdit(paymentMethod);
    setIsAddMethodModalOpen(true);
  };

  const onDeletePaymentMethod = (paymentMethodId: string) => {
    const currentMethods = paymentMethods;
    const updatedMethods = paymentMethods.filter((method) => method.id !== paymentMethodId);
    dispatch(updatePaymentMethods(updatedMethods));
    preparePaymentMethodData(updatedMethods);
    dispatch(deletePaymentMethod(paymentMethodId)).then((result) => {
      if (deletePaymentMethod.fulfilled.match(result)) {
        setShowSnackbarMsg({ type: 'success', msg: 'Successfully removed payment method' });
      }
      if (deletePaymentMethod.rejected.match(result)) {
        dispatch(updatePaymentMethods(currentMethods));
        preparePaymentMethodData(currentMethods);
        setShowSnackbarMsg({ type: 'error', msg: 'Something went wrong' });
      }
    });
  };

  const renderAction = (paymentMethod: any) => {
    return (
      <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <IconButton
            onClick={() => makePaymentMethodDefault(paymentMethod.id)}
            disabled={paymentMethod.isDefault || editingPaymentMethod}
          >
            {paymentMethod.isDefault ? <StarIcon sx={{ color: '#436EB2' }} /> : <StarBorderOutlinedIcon />}
          </IconButton>
        </div>
        <IconButton onClick={() => onEditPaymentClick(paymentMethod.id)}>
          <ModeEditIcon />
        </IconButton>
        <IconButton disabled={paymentMethod.isDefault} onClick={() => onDeletePaymentMethod(paymentMethod.id)}>
          <DeleteOutlineOutlinedIcon />
        </IconButton>
      </div>
    );
  };
  const renderCell = (row: any, column: Column) => {
    switch (column.field) {
      case 'action':
        return renderAction(row);
      default:
        return row[column.field];
    }
  };

  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            cssSrc:
              'https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap',
          },
        ],
      }}
    >
      <Box sx={{ padding: '2.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <Typography fontWeight={400} fontSize={32} sx={{ lineHeight: '2.5rem', color: '#1D1B20' }}>
          Payment Methods
        </Typography>
        <Box>
          <BkTable
            data={preparePaymentMethodData(paymentMethods)}
            columns={columns}
            renderCell={renderCell}
            initialRowsPerPage={30}
            renderPagination={false}
          />
          <Typography fontSize={15} sx={{ fontStyle: 'italic', mt: 0.5 }}>
            3% convenience fee for CC payment method. No processing fee for ACH payment method.
          </Typography>
        </Box>
        <Box sx={{ maxWidth: '18.75rem' }}>
          <Button type="secondary" onClick={() => setIsAddMethodModalOpen(true)}>
            <AddOutlinedIcon />
            <Spacer w={8} />
            Add Method
          </Button>
        </Box>
        <AddPaymentMethod
          enforceDefault={
            paymentMethods.length === 0 ||
            paymentMethodToEdit?.metadata?.is_default === 'true'
          }
          paymentMethodToEdit={paymentMethodToEdit}
          open={isAddMethodModalOpen}
          onClose={handleAddPaymentMethod}
        />
      </Box>
    </Elements>
  );
};
